import React from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Box,
  Alert,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import CommonTable from '../../../Shared/Tables/TableV1.tsx';
import CommonBadges from '../../../Shared/Badges/index.jsx';

const ColumnMappingStep = ({
  templateParameters,
  sheetColumns,
  columnMapping,
  setColumnMapping,
  getExcelColumnName,
  sheetData,
}) => {
  const handleColumnSelect = (index, column) => {
    const updatedMapping = [...columnMapping];
    const colIndex = sheetColumns.indexOf(column);
    const currentMapping = updatedMapping[index];

    updatedMapping[index] = {
      ...currentMapping,
      sheetColumn: column,
      columnIndex: getExcelColumnName(colIndex),
      // ✅ Auto-set condition for phone_number type
      condition: currentMapping.dataType === 'phone_number'
        ? '10 or 12 digits'
        : currentMapping.condition,
    };
    setColumnMapping(updatedMapping);
  };
  const handleConditionChange = (index, condition) => {
    const updatedMapping = [...columnMapping];
    updatedMapping[index] = {
      ...updatedMapping[index],
      condition,
    };
    setColumnMapping(updatedMapping);
  };

  const getConditionOptions = (dataType) => {
    switch (dataType) {
      case 'phone_number':
        return ['10 or 12 digits'];
      case 'url':
        return ['Valid URL', 'Optional'];
      case 'text':
        return ['Required', 'Not Empty', 'Optional'];
      default:
        return ['No validation'];
    }
  };

  // Get unique values for each sheet column for filtering
  const getUniqueValuesForColumn = (columnName) => {
    const colIndex = sheetColumns.indexOf(columnName);
    if (colIndex === -1) return [];

    const uniqueSet = new Set();
    sheetData.forEach((row) => {
      if (row[colIndex]) uniqueSet.add(String(row[colIndex]));
    });
    return Array.from(uniqueSet).sort().slice(0, 50); // Limit to 50 unique values
  };

  const columns = [
    {
      accessorKey: 'parameter',
      header: '1. Parameter (Template)',
      size: 200,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{row.original.parameter}</Typography>
          {templateParameters.find((p) => p.name === row.original.parameter)?.required && (
            <CommonBadges value="Required" color="error" pill={true} />
          )}
        </Box>
      ),
    },
    {
      accessorKey: 'sheetColumn',
      header: '2. Sheet Column',
      size: 200,
      Cell: ({ row }) => (
        <FormControl fullWidth size="small">
          <Select
            value={row.original.sheetColumn}
            onChange={(e) => handleColumnSelect(row.index, e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select column</em>
            </MenuItem>
            {sheetColumns.map((col, idx) => (
              <MenuItem key={idx} value={col}>
                {col}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      accessorKey: 'columnIndex',
      header: '3. Column Index',
      size: 120,
      Cell: ({ row }) => (
        <CommonBadges
          value={row.original.columnIndex || '-'}
          color={row.original.columnIndex ? 'primary' : 'default'}
          pill={true}
        />
      ),
    },
    {
      accessorKey: 'dataType',
      header: '4. Data Type',
      size: 120,
      Cell: ({ row }) => (
        <CommonBadges
          value={row.original.dataType}
          color="secondary"
          pill={true}
          variant="outlined"
        />
      ),
    },
    {
      accessorKey: 'condition',
      header: '5. Condition',
      size: 200,
      Cell: ({ row }) => (
        <FormControl fullWidth size="small">
          <Select
            value={row.original.condition}
            onChange={(e) => handleConditionChange(row.index, e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select condition</em>
            </MenuItem>
            {getConditionOptions(row.original.dataType).map((option, idx) => (
              <MenuItem key={idx} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      accessorKey: 'filterValues',
      header: '6. Available Values (Filter Preview)',
      size: 250,
      Cell: ({ row }) => {
        if (!row.original.sheetColumn) return <Typography variant="caption">-</Typography>;

        const uniqueValues = getUniqueValuesForColumn(row.original.sheetColumn);

        if (uniqueValues.length === 0) {
          return <Typography variant="caption">No data</Typography>;
        }

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {uniqueValues.slice(0, 5).map((value, idx) => (
              <CommonBadges
                key={idx}
                value={value}
                color="#e3f2fd"
                pill={true}
                style={{ fontSize: '10px' }}
              />
            ))}
            {uniqueValues.length > 5 && (
              <CommonBadges
                value={`+${uniqueValues.length - 5} more`}
                color="#90caf9"
                pill={true}
                style={{ fontSize: '10px' }}
              />
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Map Template Parameters to Sheet Columns
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Match each template parameter to the corresponding column in your uploaded sheet. Set conditions for data validation and see available values for filtering.
      </Alert>

      <CommonTable
        columns={columns}
        data={columnMapping}
        enableColumnFilters={false}
        enableHiding={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableTopToolbar={false}
        enableBottomToolbar={false}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 300px)' } }}
      />

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Mapping Summary:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {columnMapping.map((mapping, index) => (
            <CommonBadges
              key={index}
              value={`${mapping.parameter}: ${mapping.sheetColumn || 'Not mapped'}`}
              icon={mapping.sheetColumn ? <Check fontSize="small" /> : <Close fontSize="small" />}
              color={mapping.sheetColumn ? 'success' : 'default'}
              variant="outlined"
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default ColumnMappingStep;